/* eslint-disable react-refresh/only-export-components -- intentionally exports a hook alongside its provider/components */
import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light';

const STORAGE_KEY = 'poker_dice_theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function initTheme(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch { /* localStorage unavailable */ }
  try {
    // @ts-expect-error - Telegram Web App global is untyped
    const tg = window.Telegram?.WebApp?.colorScheme;
    if (tg === 'dark' || tg === 'light') return tg;
  } catch { /* not in Telegram */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.classList.toggle('dark', theme === 'dark');
  document.body.classList.toggle('dark', theme === 'dark');

  try {
    // @ts-expect-error - Telegram Web App global is untyped
    if (window.Telegram && window.Telegram.WebApp) {
      // @ts-expect-error - Telegram Web App global is untyped
      const WebApp = window.Telegram.WebApp;
      if (theme === 'dark') {
        WebApp.setHeaderColor('#08091a');
        WebApp.setBackgroundColor('#08091a');
      } else {
        WebApp.setHeaderColor('#eef0f6');
        WebApp.setBackgroundColor('#eef0f6');
      }
      WebApp.expand();
    }
  } catch {
    // Telegram Web App not available
  }
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(initTheme);

  // Apply synchronously on first render so the pre-paint frame already has the
  // right tokens (Telegram WebApp can inject light styles otherwise)
  if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') !== theme) {
    applyTheme(theme);
  }

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch { /* localStorage unavailable */ }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
